import { OrderBatchCategory } from '../database/schema.js';
import {} from '../schema.js';
import { formatNumber, getDoseChangePercent, joinUnique, toDate } from '../util/functions.js';
import { getProductionSource } from '../util/production-source.js';
import { differenceInYears } from 'date-fns';
import {} from './fields.js';
import { compact, formatReportDate, fullNameOf, sequenceStatusLabelKey } from './shared.js';
function primaryMedicine(sequence) {
    return sequence.medicine?.[0];
}
function doseUnitOf(medicine) {
    return (medicine?.activeSubstance?.activeSubstanceDetails?.doseUnit?.ucumCode ??
        medicine?.prescribedName?.drugCatalogItem?.activeSubstanceDetails?.doseUnit?.ucumCode ??
        null);
}
function activeSubstanceNameOf(medicine) {
    return medicine?.activeSubstance?.name ?? medicine?.prescribedName?.name ?? null;
}
function patientAgeAt(sequence) {
    // the age saved with the dose wins; older prescriptions fall back to birth date to application date
    if (sequence.doseBasisAgeMonths != null)
        return Math.floor(sequence.doseBasisAgeMonths / 12);
    const birth = toDate(sequence.patient?.birthDate);
    const at = toDate(sequence.therapyDate);
    if (!birth || !at)
        return null;
    return differenceInYears(at, birth);
}
// the values the dose was calculated with; prescriptions saved before they were recorded fall back to the patient's current values
export function doseBasisOf(sequence) {
    const { doseBasisHeight, doseBasisWeight, doseBasisBsa, doseBasisGfr, doseBasisUseAibw } = sequence;
    if (doseBasisHeight != null || doseBasisWeight != null || doseBasisBsa != null || doseBasisGfr != null) {
        return {
            height: doseBasisHeight,
            weight: doseBasisWeight,
            bsa: doseBasisBsa,
            gfr: doseBasisGfr,
            useAibw: doseBasisUseAibw,
        };
    }
    const characteristic = sequence.patient?.characteristics?.[0];
    return {
        height: characteristic?.height,
        weight: characteristic?.weight,
        bsa: characteristic?.bsa,
        gfr: sequence.patient?.gfrs?.[0]?.gfr ?? characteristic?.gfr,
        useAibw: characteristic?.useAibw,
    };
}
function getDistributedItem(sequence) {
    return sequence.order?.distributedOrders?.orders?.find(item => item.sequenceId === sequence.id);
}
/** Returns the raw, unformatted value for a single field of a prescription report row. */
function rawFieldValue(sequence, fieldId) {
    const patient = sequence.patient;
    const encounter = patient?.encounters?.[0];
    const medicine = primaryMedicine(sequence);
    const sequenceBatches = sequence.batches ?? [];
    const drugBatches = sequenceBatches.filter(batch => batch.category === OrderBatchCategory.enum.ACTIVE_SUBSTANCE);
    const carrierBatches = sequenceBatches.filter(batch => batch.category === OrderBatchCategory.enum.CARRIER);
    const characteristic = patient?.characteristics?.[0];
    const distributedItem = getDistributedItem(sequence);
    switch (fieldId) {
        case 'prescriptionNumber':
            return sequence.internalId ?? null;
        case 'applicationDate':
            return sequence.therapyDate ?? null;
        case 'applicationTime':
            return sequence.time ?? null;
        case 'status':
            return sequence.status ?? null;
        case 'prescriptionType':
            return medicine?.activeSubstance?.catalogType ?? null;
        case 'origin':
            return sequence.protocolId ? 'protocol' : 'individual';
        case 'protocol':
            return sequence.protocol?.name ?? null;
        case 'cycle':
            return sequence.cycle ?? null;
        case 'day':
            return sequence.day ?? null;
        case 'prescribedBy':
            return fullNameOf(sequence.created) || null;
        case 'prescribedOn':
            return sequence.createdAt ?? null;
        case 'autIdem':
            return medicine?.autIdem ?? null;
        case 'prescriptionNote':
            return sequence.therapyNote ?? null;
        case 'pharmacyNote':
            return sequence.pharmacyNote ?? null;
        case 'patientName':
            return patient?.fullName ?? null;
        case 'hospitalId':
            return patient?.hospitalPatientId ?? null;
        case 'nationalNumber':
            return patient?.nationalId ?? null;
        case 'insuranceNumber':
            return patient?.insurance?.insuranceNumber ?? null;
        case 'dateOfBirth':
            return patient?.birthDate ?? null;
        case 'age':
            return patientAgeAt(sequence);
        case 'sex':
            return patient?.sex ?? null;
        case 'section':
            return encounter?.section?.name ?? null;
        case 'ward':
            return encounter?.ward?.name ?? null;
        case 'customer':
            return encounter?.customer?.name ?? null;
        case 'diagnosis':
            return sequence.protocol?.diagnosis?.name ?? characteristic?.diagnosis?.name ?? null;
        case 'privatePatient':
            return patient?.insurance?.privatePatient ?? null;
        case 'insuranceCompany':
            return patient?.insurance?.insuranceCompany?.name ?? null;
        case 'responsibleDoctor':
            return fullNameOf(patient?.responsibleDoctor) || null;
        case 'activeSubstance':
            return activeSubstanceNameOf(medicine);
        case 'prescribedDose':
            return medicine?.totalAmount ?? null;
        case 'doseUnit':
            return doseUnitOf(medicine);
        case 'doseBasis':
            return medicine?.calculationMethod ?? null;
        case 'standardDose':
            return medicine?.recommendedDose ?? null;
        case 'doseChangePercent':
            return getDoseChangePercent(medicine?.doseProportion);
        case 'height':
            return doseBasisOf(sequence).height ?? null;
        case 'weight':
            return doseBasisOf(sequence).weight ?? null;
        case 'weightType':
            return doseBasisOf(sequence).useAibw ?? null;
        case 'bsa':
            return doseBasisOf(sequence).bsa ?? null;
        case 'bsaFormula':
            return sequence.doseBasisBsaFormula ?? null;
        case 'gfr':
            return doseBasisOf(sequence).gfr ?? null;
        case 'gfrMethod':
            return sequence.doseBasisGfrMethod ?? null;
        case 'gfrSampleDate':
            return sequence.doseBasisGfrLabTime ?? null;
        case 'serumCreatinine':
            return sequence.doseBasisSerumCreatinine ?? null;
        case 'cumulativeDoseToDate':
            return null;
        case 'applicationForm':
            return sequence.applicationForm?.name ?? null;
        case 'carrierSolution':
            return sequence.carrierSolution?.name ?? null;
        case 'carrierVolume':
            return sequence.volume ?? null;
        case 'finalVolume':
            return null;
        case 'concentration':
            return null;
        case 'administrationDuration':
            return sequence.duration ?? null;
        case 'prescriptionTradeName':
            return medicine?.prescribedName?.name ?? null;
        case 'doctorValidatedBy':
            return fullNameOf(sequence.doctorValidated) || null;
        case 'doctorValidatedOn':
            return sequence.doctorValidatedAt ?? null;
        case 'pharmacistValidatedBy':
            return fullNameOf(sequence.pharmacistValidated) || null;
        case 'pharmacistValidatedOn':
            return sequence.pharmacistValidatedAt ?? null;
        case 'preparationType':
            return sequence.preparationType ?? null;
        case 'pickingListNumber':
            return sequence.order?.internalId ?? null;
        case 'tradeNameUsed':
            return joinUnique(drugBatches.map(batch => batch.batch?.tradeName?.name));
        case 'batchNumber':
            return joinUnique(drugBatches.map(batch => batch.batch?.lotNumber));
        case 'batchExpiry':
            return drugBatches[0]?.batch?.expiryDate ?? null;
        case 'carrierBatchNumber':
            return joinUnique(carrierBatches.map(batch => batch.batch?.lotNumber));
        case 'carrierBatchExpiry':
            return carrierBatches[0]?.batch?.expiryDate ?? null;
        case 'quantityUsed': {
            const quantity = distributedItem?.activeSubstances.batches.reduce((sum, batch) => sum + batch.quantity, 0);
            return quantity ? quantity : null;
        }
        case 'quantityUnit':
            return doseUnitOf(medicine);
        case 'source':
            return getProductionSource(sequence);
        case 'restVialNumber':
            return null;
        case 'restVialCreated':
            return null;
        case 'producedBy':
            return null;
        case 'producedOn':
            return sequence.producedAt ?? null;
        case 'useBy':
            return null;
        case 'productionDay':
            // the day the picking list was created, the same date the Reports date basis filters on
            return sequence.order?.createdAt ?? null;
        case 'productionWorkstation':
            return sequence.order?.productionLocation?.name ?? null;
        case 'qaResult':
            return null;
        case 'qaRejectionReason':
            // the note says what Other means (CY-340)
            return [sequence.discardReason, sequence.discardNote].filter(Boolean).join(': ') || null;
        case 'releasedBy':
            return null;
        case 'releasedOn':
            return sequence.releasedAt ?? null;
        case 'dispatchedOn':
            return null;
        case 'administeredBy':
            return fullNameOf(sequence.administeredByUser) || null;
        case 'administeredOn':
            return sequence.administeredAt ?? null;
        case 'administrationMode':
            return sequence.administrationMode ?? null;
        case 'scanCheck':
            // Only Scan mode has a patient scan to report on.
            if (sequence.administrationMode !== 'SCAN')
                return null;
            return sequence.patientScanSkipped ? 'patient_skipped' : 'completed';
        case 'notAdministeredReason':
            return sequence.discardReason ?? null;
        case 'returnedOn':
            return sequence.returnedAt ?? null;
        case 'unitPrice':
            return null;
        case 'costOfProductUsed':
            return null;
        case 'priorApproval': {
            const requiresApproval = medicine?.activeSubstance?.activeSubstanceDetails?.additionalApprovalRequired ??
                medicine?.prescribedName?.drugCatalogItem?.activeSubstanceDetails?.additionalApprovalRequired;
            if (requiresApproval == null)
                return null;
            if (!requiresApproval)
                return 'not_required';
            if (sequence.status === 'pending_approval')
                return 'pending';
            if (sequence.status === 'budget_not_approved')
                return null;
            return 'approved';
        }
        case 'hospitalSystemOrderNumber':
            return null;
        case 'hospitalSystemMessage':
            return null;
        default:
            return null;
    }
}
/**
 * Fields whose value is a machine token rather than display text; each maps to an i18n key so the
 * screen never shows a raw enum (`in_production`, `GRAVIMETRIC`, `ABSOLUTE`, `yes`/`no`, ...).
 */
function translateFieldValue(sequence, fieldId, raw, tr) {
    switch (fieldId) {
        case 'status': {
            const key = sequenceStatusLabelKey(sequence);
            return key ? tr.activity(`${key}.title`) : null;
        }
        case 'sex':
        case 'doseBasis':
        case 'preparationType':
        case 'prescriptionType':
            return tr.common(String(raw));
        case 'origin':
            return tr.report(`origin_${raw}`);
        case 'source':
            return tr.report(`source_${raw}`);
        case 'priorApproval':
            return tr.report(`prior_approval_${raw}`);
        case 'administrationMode':
            return tr.common(`settings.administrationMode.${String(raw).toLowerCase()}`);
        case 'scanCheck':
            return tr.report(`scan_check_${raw}`);
        case 'weightType':
            return tr.report(raw ? 'weight_type_aibw' : 'weight_type_actual');
        case 'bsaFormula':
            return tr.common(`bsa_formula.${String(raw)}`);
        case 'gfrMethod':
            return tr.common(`gfr.${String(raw)}`);
        case 'serumCreatinine': {
            const unit = sequence.doseBasisSerumUnit ? tr.common(`serum_unit.${sequence.doseBasisSerumUnit}`) : null;
            return [formatNumber(raw, true), unit].filter(Boolean).join(' ');
        }
        case 'autIdem':
        case 'privatePatient':
        case 'restVialCreated':
            return tr.common(raw ? 'yes' : 'no');
        default:
            return null;
    }
}
/** Formats a single field to the string shown in a cell line (values translated, dates DD.MM.YYYY, ...). */
export function formatPrescriptionField(sequence, field, tr) {
    const raw = rawFieldValue(sequence, field.id);
    if (raw === null || raw === undefined || raw === '')
        return null;
    const translated = translateFieldValue(sequence, field.id, raw, tr);
    if (translated !== null)
        return translated;
    switch (field.type) {
        case 'date':
            return formatReportDate(raw);
        case 'number':
            return typeof raw === 'number' ? formatNumber(raw, Boolean(field.decimals)) : String(raw);
        default:
            return String(raw);
    }
}
/**
 * One stacked line per cell. Fields that share a `cellGroup` collapse onto the design's lines:
 * patient name over "HID x", protocol over "C1 D1", substance over "92 mg", quantity on one line.
 */
export function getPrescriptionCellLines(sequence, column, tr) {
    const valueOf = (id) => {
        const field = column.fields.find(candidate => candidate.id === id);
        return field ? formatPrescriptionField(sequence, field, tr) : null;
    };
    switch (column.fields[0]?.cellGroup) {
        case 'patient': {
            const hospitalId = valueOf('hospitalId');
            return compact([valueOf('patientName'), hospitalId ? `HID ${hospitalId}` : null]);
        }
        case 'protocol': {
            const cycle = valueOf('cycle');
            const day = valueOf('day');
            const cycleDay = [cycle ? `C${cycle}` : null, day ? `D${day}` : null].filter(Boolean).join(' ');
            return compact([valueOf('protocol'), cycleDay || null]);
        }
        case 'substance': {
            const dose = valueOf('prescribedDose');
            const unit = valueOf('doseUnit');
            return compact([valueOf('activeSubstance'), [dose, unit].filter(Boolean).join(' ') || null]);
        }
        case 'tradeName':
            return compact([valueOf('tradeNameUsed'), valueOf('batchNumber')]);
        case 'quantity': {
            const quantity = valueOf('quantityUsed');
            const unit = valueOf('quantityUnit');
            const line = [quantity, unit].filter(Boolean).join(' ');
            return line ? [line] : [];
        }
        default:
            return compact(column.fields.map(field => formatPrescriptionField(sequence, field, tr)));
    }
}
//# sourceMappingURL=prescription-values.js.map